// The sea page: the season timelapse of a closed season (chronicle.md
// §9.5, R3300).
//
// A visitor opens `/?timelapse=<world>.<season>`, the link of the season
// result post of a clan (player-reach.md §3.9) or THE SEA of the LAST
// SEASON card, and watches the territory map of that season. The page
// needs no session and no share: `fast/main.ts` shows it before the entry
// resolves, so the landing screen, the hub and the world socket never
// start. It reads the public sea route (§10.6), which serves a closed
// season alone and names no fleet and no player.
//
// The page is THE SEA card of the public reader (`ui/timelapse/reader.ts`)
// with no commander: the legend marks no faction. Every miss of the route
// (an unknown world, a live season, a season with no frame) shows the one
// "no public map" state, and so does a byte string that does not decode.
//
// The page wears the hub's stylesheet, as the public reader does: its root
// is `#hub`, and the game page already loads `hub.css` through `hub.ts`.

import { linkButton } from '../../choice/choice';
import { ApiError, STATUS_SEA_UNKNOWN, getSeaTimelapse, type SeaTimelapse } from '../../../net/api';
import { decodeTimelapse, type Timelapse } from '../../../net/timelapse';
import { ReaderTimelapse } from '../../timelapse/reader';
import { chronicle as C, timelapse as T } from '../../../gen/strings';
import { card, empty, esc, failed, h1, lede, loading, page } from '../panel';
import { pageBase, type SeaAddress } from './address';

/** What the sea page holds: the read in flight, the decoded season, a
 *  miss, or another refusal. */
export type SeaState =
  | { kind: 'loading' }
  | { kind: 'ready'; worldName: string; timelapse: Timelapse }
  | { kind: 'none' }
  | { kind: 'failed'; error: unknown };

/** The place of THE SEA card on the page. */
const TIMELAPSE_HOST = '<div class="tlhost"></div>';

/** The glyph of the card of a state with no map. */
const GLYPH = '≈';

/** The title of the page: the world where the read named one. */
export function seaTitle(s: SeaState): string {
  return s.kind === 'ready' && s.worldName !== '' ? T.sea_title({ world: s.worldName }) : T.sea_page;
}

/** The state of one answer of the route: a season with no frame, or a
 *  byte string that does not decode, is a miss. */
export function seaStateOf(read: SeaTimelapse): SeaState {
  let tl: Timelapse | null = null;
  try {
    tl = decodeTimelapse(read.data);
  } catch {
    tl = null;
  }
  return tl === null || tl.frames.length === 0
    ? { kind: 'none' }
    : { kind: 'ready', worldName: read.worldName, timelapse: tl };
}

/** The state of a refusal: the 404 of the route is a miss. */
export function seaRefusal(error: unknown): SeaState {
  return error instanceof ApiError && error.status === STATUS_SEA_UNKNOWN ? { kind: 'none' } : { kind: 'failed', error };
}

/** The whole page as markup. Pure, so a test reads it with no DOM. */
export function seaHtml(a: SeaAddress, s: SeaState, base = pageBase()): string {
  const home = `<div class="chronbar">${linkButton(C.reader_home, base, { variant: 'primary' })}</div>`;
  const sub = T.sea_season({ season: a.season });
  if (s.kind === 'loading') return page(h1(T.sea_page, sub), card(T.sea_page, GLYPH, '', loading()), home);
  if (s.kind === 'none') {
    return page(h1(T.sea_page, sub), card(T.sea_page, GLYPH, '', empty(T.sea_none, T.sea_none_why)), home);
  }
  if (s.kind === 'failed') return page(h1(T.sea_page, sub), card(T.sea_page, GLYPH, '', failed(s.error)), home);
  const out = `<div class="chronshare"><span class="tag">${esc(C.closed_tag)}</span>${lede(T.sea_what)}</div>`
    + TIMELAPSE_HOST;
  return page(h1(seaTitle(s), sub), out, home);
}

/** Shows the sea page of one closed season. It never resolves: the page
 *  is the whole page, and a link leaves it. */
export function showSea(
  a: SeaAddress,
  read: (world: number, season: number) => Promise<SeaTimelapse> = getSeaTimelapse,
): void {
  const root = document.createElement('div');
  root.id = 'hub';
  root.className = 'reader';
  const pane = document.createElement('div');
  pane.className = 'pane';
  const inner = document.createElement('div');
  inner.className = 'paneinner';
  pane.append(inner);
  root.append(pane);
  document.body.appendChild(root);
  const draw = (s: SeaState): void => {
    inner.innerHTML = seaHtml(a, s);
    document.title = seaTitle(s);
    if (s.kind !== 'ready') return;
    const host = inner.querySelector('.tlhost');
    if (!host) return;
    // No commander: the legend marks no faction (chronicle.md §9.5).
    const sea = new ReaderTimelapse(s.timelapse, 0, '');
    host.replaceWith(sea.el);
    sea.draw();
  };
  draw({ kind: 'loading' });
  read(a.world, a.season).then((r) => draw(seaStateOf(r)), (error: unknown) => draw(seaRefusal(error)));
}
